import React from 'react';
import PropTypes from 'prop-types';
import { Routes, Route } from 'react-router-dom';
import { connect } from 'react-redux';
import DOMPurify from 'dompurify';

import { RESULTS, HOMEWORK_EXECUTION, PAUSE, START_PAGE, CLASSWORK_LOGIN } from '../../constants/routes';

import { showModal } from '../../actions/modal';

import StatusBar from '../../components/status-bar';
import Section, { SectionRow, BreakText, Text, Link, TextBold } from '../../components/status-bar/section';
import UserAvatar from '../sidebar-container/assets/userAvatar.svg?react';
import SupportIcon from '../../components/status-bar/assets/support.svg?react';
import InformationSource from '../../components/status-bar/assets/informationsource.svg?react';
import { RATING_MODAL, REPORT_MODAL } from '../modal/modal-types';
import Difficulty from '../../components/difficulty';
import { getExercisePrompt, getExerciseTypeFromName } from '../../lib/exercise';

import styled from 'styled-components';
import FluidContainer from '../../components/layouts/components/fluid-container';

import EventIcon from './assets/event.svg?react';
import EventCancelledIcon from './assets/eventCancelled.svg?react';
import { parseDate } from '../../lib/utils';
import Stack from '../../components/stack';
import { parseHomeworkId } from '../../lib/homework';
import { useOnlineStatus } from '../../lib/hooks';

export const ExerciseType = styled.p`
    text-overflow: ellipsis;
    overflow: hidden;
    font-size: 14px;
    margin: 0 0 4px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.16px;
    line-height: 20px;
`;

const DescriptionContainer = styled.div`
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.16px;
`;

export const StatusBarHomeworkSection = ({ title, homework, assignment, source }) => {
    const homeworkId = parseHomeworkId(homework.id, homework.idCat, source);

    const isAssignment = !!assignment?.title;
    const executionType = isAssignment ? 'Prova assegnata' : 'Prova in autonomia';

    const creator = homework.ugc ? `${homework.creator?.firstName} ${homework.creator?.lastName}` : 'Zanichelli';

    return (
        <Section title={title} Icon={isAssignment ? EventIcon : EventCancelledIcon}>
            <SectionRow>
                <TextBold>{executionType}</TextBold>
            </SectionRow>
            <SectionRow>
                <Text dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(homework.publicTitle) }} />
            </SectionRow>
            <Stack gap="xs">
                <SectionRow>
                    <TextBold>{'Creata da: '}</TextBold>
                    <Text>{creator}</Text>
                </SectionRow>
                {homework.description && (
                    <SectionRow>
                        <DescriptionContainer>
                            <TextBold>{'Descrizione: '}</TextBold>
                            <BreakText>{homework.description}</BreakText>
                        </DescriptionContainer>
                    </SectionRow>
                )}
                {!!assignment?.teacherName && (
                    <SectionRow>
                        <TextBold>{'Assegnata da:'}</TextBold>
                        <Text>{assignment.teacherName}</Text>
                    </SectionRow>
                )}
                {!!assignment?.endDate && (
                    <SectionRow>
                        <TextBold>{'Scadenza:'}</TextBold>
                        <Text>{parseDate(assignment.endDate, 'DD/MM/YYYY - HH:mm')}</Text>
                    </SectionRow>
                )}
                <SectionRow>
                    <TextBold>{'Prova (ID):'}</TextBold>
                    <Text id="homeworkId">{`#${homeworkId}`}</Text>
                </SectionRow>
            </Stack>
        </Section>
    );
};

StatusBarHomeworkSection.propTypes = {
    title: PropTypes.string,
    homework: PropTypes.object,
    assignment: PropTypes.object,
    source: PropTypes.string,
};

const StatusBarContainer = ({ showModal, currentIndex, exercises, homework, assignment, status, source, singleAnswer, user }) => {
    const currentExercise = exercises[currentIndex] || {};

    const showReportModal = (e) => {
        e.preventDefault();
        showModal(REPORT_MODAL);
    };

    const showRatingModal = (e) => {
        e.preventDefault();
        showModal(RATING_MODAL);
    };

    const ExerciseInfoSection = ({ currentExercise, singleAnswer }) => {
        return (
            <Section title="Esercizio" Icon={InformationSource}>
                <FluidContainer bg="surface-grey-200" py="small" pr="mobile" pl="tablet" ml={-24} borderRadius={4} flexDirection="column">
                    <ExerciseType>{getExerciseTypeFromName(currentExercise.typeLabel)}</ExerciseType>
                    <Text>
                        <TextBold style={{ marginRight: '4px' }}>{'Cosa devi fare:'}</TextBold>
                        {getExercisePrompt(currentExercise, singleAnswer)}
                    </Text>
                </FluidContainer>
                <SectionRow style={{ justifyContent: 'space-between' }}>
                    <SectionRow>
                        <TextBold>{'Difficoltà:'}</TextBold>
                        <Difficulty level={currentExercise.difficulty} />
                    </SectionRow>
                    <SectionRow>
                        <TextBold>{'Esercizio (ID):'}</TextBold>
                        <Text>{`#${currentExercise.originalId || currentExercise.id}`}</Text>
                    </SectionRow>
                </SectionRow>
            </Section>
        );
    };

    ExerciseInfoSection.propTypes = {
        currentExercise: PropTypes.object,
        singleAnswer: PropTypes.bool,
    };

    const SupportSection = () => {
        const onlineStatus = useOnlineStatus();
        return (
            <Section title="Supporto" Icon={SupportIcon}>
                <SectionRow>
                    <Text>{'Hai bisogno di aiuto?'}</Text>
                    <Link href="#" onClick={showReportModal} disabled={!onlineStatus}>
                        {'Segnala un problema'}
                    </Link>
                </SectionRow>
                <SectionRow>
                    <Text>{'Ci aiuti a migliorare?'}</Text>
                    <Link href="#" onClick={showRatingModal} disabled={!onlineStatus}>
                        {'Lasciaci il tuo parere'}
                    </Link>
                </SectionRow>
            </Section>
        );
    };

    const UserInfoSection = ({ user, assignment }) => {
        if (user.isLoggedIn)
            return (
                <Section title={`${user.firstName} ${user.lastName}`} Icon={UserAvatar}>
                    <Stack gap="small">
                        <SectionRow>
                            <Text>{user.email}</Text>
                        </SectionRow>

                        {!!assignment?.className && (
                            <SectionRow>
                                <TextBold>{'Classe:'}</TextBold>
                                <Text>{assignment.className}</Text>
                            </SectionRow>
                        )}
                    </Stack>
                </Section>
            );
        return <></>;
    };

    return (
        <Routes>
            <Route
                path={RESULTS}
                element={
                    <StatusBar>
                        <StatusBarHomeworkSection title="Prova terminata" homework={homework} assignment={assignment} source={source} />
                        <UserInfoSection user={user} assignment={assignment} />
                        <SupportSection />
                    </StatusBar>
                }
            />
            <Route
                path={PAUSE}
                element={
                    <StatusBar>
                        <StatusBarHomeworkSection
                            title={!status.resumed ? 'Prova in pausa' : 'Prova in corso'}
                            homework={homework}
                            assignment={assignment}
                            source={source}
                        />
                        <UserInfoSection user={user} assignment={assignment} />
                        <SupportSection />
                    </StatusBar>
                }
            />
            <Route
                path={START_PAGE}
                element={
                    <StatusBar>
                        <StatusBarHomeworkSection title="Prova selezionata" homework={homework} assignment={assignment} source={source} />
                        <UserInfoSection user={user} assignment={assignment} />
                        <SupportSection />
                    </StatusBar>
                }
            />
            <Route
                path={HOMEWORK_EXECUTION}
                element={
                    <StatusBar>
                        <ExerciseInfoSection currentExercise={currentExercise} singleAnswer={singleAnswer} />
                        <StatusBarHomeworkSection
                            title={status.finished ? 'Prova terminata' : 'Prova in corso'}
                            homework={homework}
                            assignment={assignment}
                            source={source}
                        />
                        <UserInfoSection user={user} assignment={assignment} />
                        <SupportSection />
                    </StatusBar>
                }
            />
            <Route
                path={CLASSWORK_LOGIN}
                element={
                    <StatusBar>
                        <SupportSection />
                    </StatusBar>
                }
            />
        </Routes>
    );
};

StatusBarContainer.propTypes = {
    showModal: PropTypes.func,
    currentIndex: PropTypes.number,
    exercises: PropTypes.array,
    homework: PropTypes.object,
    assignment: PropTypes.object,
    singleAnswer: PropTypes.bool,
    status: PropTypes.object,
    source: PropTypes.string,
    user: PropTypes.object,
};

const mapStateToProps = (state) => {
    return {
        currentIndex: state.status.currentExerciseIndex,
        exercises: state.homework.exercises,
        homework: state.homework,
        assignment: state.assignment,
        singleAnswer: state.application.singleAnswer,
        status: state.status,
        source: state.application.source,
        user: state.auth,
    };
};

const actionCreators = {
    showModal,
};

export default connect(mapStateToProps, actionCreators)(StatusBarContainer);
